import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  LogOut,
  Mail,
  Phone,
  Calendar as CalIcon,
  MessageSquare,
  QrCode,
  Download,
  Copy,
  Check,
  TrendingUp,
} from "lucide-react";
import { Ornament } from "./Ornament";
import Seo from "./Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "lumiere.admin.pw";

function LoginGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, null, {
        headers: { "x-admin-password": pw },
      });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onSuccess(pw);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Wrong password." : "Server error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="min-h-screen bg-[var(--lu-black)] grain flex items-center justify-center px-6"
      data-testid="dashboard-login"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md border border-[var(--lu-gold)]/50 bg-[var(--lu-onyx)]/60 p-10"
      >
        <p className="font-micro text-[10px] tracking-[0.3em] text-[var(--lu-gold)]">
          Private Studio
        </p>
        <h1 className="mt-3 font-serif text-[36px] text-[var(--lu-ivory)] leading-tight">
          Dashboard <span className="italic text-[var(--lu-gold)]">Access</span>
        </h1>
        <Ornament className="mt-6" light />
        <label className="mt-8 block font-micro text-[10px] tracking-[0.3em] text-[var(--lu-gold)] uppercase">
          Password
        </label>
        <input
          type="password"
          value={pw}
          autoFocus
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          data-testid="dashboard-password-input"
          className="mt-3 w-full bg-transparent border-0 border-b border-[var(--lu-gold)]/60 focus:border-[var(--lu-gold)] focus:outline-none text-[var(--lu-ivory)] text-[18px] py-2"
        />
        {err && (
          <p className="mt-4 text-[13px] text-red-400" data-testid="dashboard-login-error">
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !pw}
          data-testid="dashboard-login-submit"
          className="btn-gold mt-10 w-full disabled:opacity-50"
        >
          {loading ? "Unlocking…" : "Enter"}
        </button>
        <p className="mt-8 text-[11px] text-ivory-40 text-center">
          Private area · Lumière Entertainment
        </p>
      </form>
    </section>
  );
}

/* -------------------- Stat / KPI card -------------------- */
function Kpi({ label, value, hint }) {
  return (
    <div className="border border-[var(--lu-gold)]/40 bg-[var(--lu-onyx)]/40 p-6">
      <p className="font-micro text-[10px] tracking-[0.3em] text-[var(--lu-gold)] uppercase">
        {label}
      </p>
      <p className="mt-3 font-serif text-[46px] leading-none text-[var(--lu-ivory)]">
        {value}
      </p>
      {hint && (
        <p className="mt-2 text-[11px] text-ivory-50 font-light">{hint}</p>
      )}
    </div>
  );
}

/* -------------------- Breakdown table (label + count + bar) -------------------- */
function Breakdown({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-[var(--lu-gold)]/40 bg-[var(--lu-onyx)]/40 p-6">
      <div className="flex items-center gap-2 text-[var(--lu-gold)]">
        <TrendingUp size={14} />
        <p className="font-micro text-[10px] tracking-[0.3em] uppercase">
          {title}
        </p>
      </div>
      <ul className="mt-6 space-y-4">
        {rows.length === 0 && (
          <li className="text-[13px] text-ivory-50 italic">No data yet.</li>
        )}
        {rows.slice(0, 8).map((r) => (
          <li key={r.key}>
            <div className="flex items-baseline justify-between gap-4">
              <span className="font-serif text-[15px] text-[var(--lu-ivory)] truncate">
                {r.key}
              </span>
              <span className="font-micro text-[11px] text-[var(--lu-gold)]">
                {r.count}
              </span>
            </div>
            <div className="mt-2 h-[3px] bg-[var(--lu-gold)]/15">
              <div
                className="h-full bg-[var(--lu-gold)]"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------- Inquiry card -------------------- */
function InquiryCard({ q, idx }) {
  const [copied, setCopied] = useState(false);
  const dt = q.created_at ? new Date(q.created_at) : null;
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <article
      className="border border-[var(--lu-gold)]/30 bg-[var(--lu-onyx)]/30 p-6"
      data-testid={`inquiry-card-${idx}`}
    >
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-serif text-[20px] text-[var(--lu-ivory)] truncate">
            {q.name}
          </p>
          <p className="mt-1 text-[12px] text-ivory-50">
            {dt ? dt.toLocaleString() : "—"}
            {q.source ? (
              <>
                {" · "}
                <span className="text-[var(--lu-gold)]">{q.source}</span>
              </>
            ) : null}
          </p>
        </div>
        {q.event_type && (
          <span className="shrink-0 font-micro text-[9px] tracking-[0.24em] uppercase text-[var(--lu-gold)] border border-[var(--lu-gold)]/50 px-2 py-1">
            {q.event_type}
          </span>
        )}
      </header>

      <div className="mt-4 grid gap-2 text-[13px]">
        <a
          href={`mailto:${q.email}`}
          className="flex items-center gap-2 text-[var(--lu-ivory)] hover:text-[var(--lu-gold)]"
        >
          <Mail size={13} className="text-[var(--lu-gold)]" /> {q.email}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              copy(q.email);
            }}
            aria-label="Copy email"
            className="ml-auto text-ivory-50 hover:text-[var(--lu-gold)]"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </a>
        {q.phone && (
          <a
            href={`tel:${q.phone.replace(/\s+/g, "")}`}
            className="flex items-center gap-2 text-[var(--lu-ivory)] hover:text-[var(--lu-gold)]"
          >
            <Phone size={13} className="text-[var(--lu-gold)]" /> {q.phone}
          </a>
        )}
        {q.event_date && (
          <p className="flex items-center gap-2 text-ivory-70">
            <CalIcon size={13} className="text-[var(--lu-gold)]" /> {q.event_date}
          </p>
        )}
      </div>

      {q.message && (
        <div className="mt-4 pl-3 border-l-2 border-[var(--lu-gold)] text-[13px] text-ivory-75 leading-[1.7] whitespace-pre-wrap">
          <MessageSquare
            size={12}
            className="inline text-[var(--lu-gold)] -mt-0.5 mr-1.5"
          />
          {q.message}
        </div>
      )}
    </article>
  );
}

/* -------------------- QR panel -------------------- */
function QrPanel() {
  return (
    <div
      className="border border-[var(--lu-gold)]/50 bg-[var(--lu-ivory)] p-6 flex flex-col items-center text-center"
      data-testid="dashboard-qr-panel"
    >
      <div className="flex items-center gap-2 text-[var(--lu-gold-dark)]">
        <QrCode size={14} />
        <p className="font-micro text-[10px] tracking-[0.3em] uppercase">
          Contact QR
        </p>
      </div>
      <img
        src="/generated/qr-contact.png"
        alt="QR code to Lumière Entertainment contact page"
        className="mt-5 w-full max-w-[220px]"
        loading="lazy"
      />
      <p className="mt-4 text-[11px] text-black-70 font-light leading-relaxed">
        Print on business cards, event signage or flyers. Every scan is tagged{" "}
        <span className="italic text-[var(--lu-gold-dark)]">utm_source=qr</span>.
      </p>
      <div className="mt-4 flex flex-col gap-2 w-full">
        <a
          href="/generated/qr-contact.png"
          download="lumiere-qr.png"
          className="btn-gold text-[10px] px-4 py-2 inline-flex items-center justify-center gap-2"
          data-testid="download-qr-png"
        >
          <Download size={12} /> Download PNG
        </a>
        <a
          href="/generated/qr-contact.svg"
          download="lumiere-qr.svg"
          className="btn-gold-outline text-[10px] px-4 py-2 inline-flex items-center justify-center gap-2"
          data-testid="download-qr-svg"
        >
          <Download size={12} /> Download SVG (print-quality)
        </a>
      </div>
    </div>
  );
}

/* -------------------- Dashboard shell -------------------- */
export default function DashboardPage() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async (password) => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/inquiries`, {
        headers: { "x-admin-password": password },
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setPw("");
      } else {
        setError("Failed to load dashboard.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pw) fetchData(pw);
  }, [pw]);

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPw("");
    setData(null);
  };

  if (!pw) return <LoginGate onSuccess={setPw} />;

  return (
    <>
      <Seo
        path="/dashboard"
        title="Dashboard — Lumière Entertainment"
        description="Private studio dashboard for Lumière Entertainment."
      />
      <section
        className="min-h-screen bg-[var(--lu-black)] pt-32 pb-24 grain"
        data-testid="dashboard-page"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Header */}
          <header className="flex flex-wrap items-end justify-between gap-6 pb-10 border-b border-[var(--lu-gold)]/30">
            <div>
              <p className="font-micro text-[10px] tracking-[0.3em] text-[var(--lu-gold)]">
                Private Studio
              </p>
              <h1 className="mt-3 font-serif text-[46px] sm:text-[60px] text-[var(--lu-ivory)] leading-none">
                Dashboard
              </h1>
              <p className="mt-3 text-[13px] text-ivory-60 font-light">
                Every inquiry, every source, every night — in one glance.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="btn-gold-outline text-[10px] px-5 py-2"
                data-testid="dashboard-view-site"
              >
                View Site
              </Link>
              <button
                type="button"
                onClick={logout}
                data-testid="dashboard-logout"
                className="btn-gold text-[10px] px-5 py-2 inline-flex items-center gap-2"
              >
                <LogOut size={12} /> Log out
              </button>
            </div>
          </header>

          {loading && !data && (
            <p className="mt-16 text-center text-ivory-50">Loading…</p>
          )}
          {error && (
            <p className="mt-16 text-center text-red-400">{error}</p>
          )}

          {data && (
            <>
              {/* KPIs */}
              <div
                className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4"
                data-testid="dashboard-kpis"
              >
                <Kpi
                  label="Total Inquiries"
                  value={data.stats.total}
                  hint="All time"
                />
                <Kpi
                  label="Last 7 Days"
                  value={data.stats.last_7_days}
                  hint="Fresh leads"
                />
                <Kpi
                  label="Sources"
                  value={data.by_source.length}
                  hint={
                    data.by_source[0]
                      ? `Top: ${data.by_source[0].key}`
                      : "—"
                  }
                />
              </div>

              {/* Breakdowns + QR */}
              <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1fr_20rem] gap-4">
                <Breakdown title="By Source" rows={data.by_source} />
                <Breakdown title="By Event Type" rows={data.by_event_type} />
                <QrPanel />
              </div>

              {/* Recent inquiries */}
              <div className="mt-14">
                <div className="flex items-end justify-between gap-4 mb-6">
                  <div>
                    <p className="font-micro text-[10px] tracking-[0.3em] text-[var(--lu-gold)]">
                      Inbox
                    </p>
                    <h2 className="mt-2 font-serif text-[32px] sm:text-[42px] text-[var(--lu-ivory)]">
                      Recent Inquiries
                    </h2>
                  </div>
                  <p className="font-micro text-[10px] text-ivory-50 uppercase tracking-[0.24em]">
                    {data.inquiries.length} shown
                  </p>
                </div>

                {data.inquiries.length === 0 ? (
                  <div className="border border-[var(--lu-gold)]/30 bg-[var(--lu-onyx)]/30 p-10 text-center">
                    <p className="font-serif italic text-[18px] text-ivory-70">
                      No inquiries yet — your first golden evening awaits.
                    </p>
                  </div>
                ) : (
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    data-testid="dashboard-inquiries"
                  >
                    {data.inquiries.map((q, i) => (
                      <InquiryCard key={q.id || i} q={q} idx={i} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
